// src/ApiPanel/components/reports/TransactionReport.jsx
// The engine behind every Transaction Report screen.
//
// WHY THE COLUMNS ARE DERIVED, NOT DECLARED
// -----------------------------------------
// These endpoints do not return a stable row shape. Each one branches on the
// caller's role and builds a different dict — a Retailer gets `commission`,
// `tds`, `charge`; a White Label gets `"Opening Balance"`, `"Commission"`,
// `"TDS"`, `"Closing Balance"`; an Admin gets those plus `"BC Name"` and a
// second set of BC-prefixed columns. Casing and spacing are inconsistent
// between products too (`ref_no` here, `order_id` there, `txn_id` elsewhere).
//
// Hardcoding a column list per report per role would be roughly sixty
// definitions to keep in sync with a file that is still changing. So the
// table reads the keys off the rows it actually received, orders the ones it
// recognises, appends the rest, and hides the handful that should never be
// shown. Add a role tomorrow and the screen keeps working.
//
// WHAT THE ENDPOINTS DO NOT GIVE US
// ---------------------------------
// No pagination, no totals, no server-side search — the response is a bare
// {"data": [...]} with every matching row. So the count, the totals and the
// search box here are all computed in the browser over the full set. Totals
// are summed in integer paise rather than floats, so a long list cannot drift
// a rupee, and the footnote says plainly that they were computed here.
import React, { useEffect, useMemo, useState } from "react";
import {
  Badge, Card, DateField, ErrorState, FilterBar, Notice, ReportPage, Stat,
  StatGrid, TextField, count, daysAgoISO, money, todayISO,
} from "./ReportKit";
import { apiPanelFetch, qs } from "../../utils/apiPanelFetch";
import { useApiPanelSession } from "../../context/ApiPanelSessionContext";

/** Keys never rendered: internal plumbing, raw provider payloads, file paths. */
const HIDDEN = new Set([
  "modified_at", "api_response", "pid", "photo", "signature",
  "panformfront", "panformback", "aadharfront", "aadharback",
  "doc1", "doc2", "slip", "bcid",
]);

/** Preferred left-to-right order for keys we recognise. Unknown keys follow. */
const ORDER = [
  "id", "created_at", "txn_id", "ref_no", "order_id", "ack_no", "opid",
  "bc name", "phone", "user_name", "retailer_name", "operator", "product",
  "transection_type", "type", "transection flow", "number", "sender_phone",
  "receiver_phone", "sender_name", "receiver_name", "bank name", "bank_rrn",
  "card_number", "amount", "opening balance", "opening_balance", "commission",
  "tds", "charge", "gst", "closing balance", "closing_balance", "remark",
  "note", "status",
];

/** Right-align and total these. */
const MONEYISH = /(amount|balance|commission|tds|charge|gst|profit)/i;

const label = (key) =>
  key.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()).trim();

/** Sum a money column without float drift, by working in integer paise. */
function sumPaise(rows, key) {
  let paise = 0;
  let sawNumber = false;
  for (const row of rows) {
    const raw = row[key];
    if (raw === null || raw === undefined || raw === "") continue;
    const n = Number(String(raw).replace(/,/g, ""));
    if (Number.isNaN(n)) continue;
    sawNumber = true;
    paise += Math.round(n * 100);
  }
  return sawNumber ? (paise / 100).toFixed(2) : null;
}

export default function TransactionReport({
  title, subtitle, endpoint, notice, defaultDays = 6,
}) {
  const { user } = useApiPanelSession();
  const [filters, setFilters] = useState(() => ({
    filter_date: daysAgoISO(defaultDays),
    end_date: todayISO(),
  }));
  const [applied, setApplied] = useState(() => ({
    filter_date: daysAgoISO(defaultDays),
    end_date: todayISO(),
  }));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    apiPanelFetch(`${endpoint}${qs({ user: user.id, ...applied })}`)
      .then((res) => {
        if (cancelled) return;
        // These endpoints answer {"data": [...]} — and on a bad role they
        // answer {"data": [], "error": "Unknown role: X"} with a 400, which
        // apiPanelFetch already turns into a thrown message.
        setRows(Array.isArray(res?.data) ? res.data : []);
      })
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [endpoint, applied, user?.id]);

  const columns = useMemo(() => {
    const seen = new Map();
    for (const row of rows.slice(0, 50)) {
      Object.keys(row || {}).forEach((k) => {
        if (!HIDDEN.has(k.toLowerCase())) seen.set(k, true);
      });
    }
    const keys = [...seen.keys()];
    const rank = (k) => {
      const i = ORDER.indexOf(k.toLowerCase());
      return i === -1 ? ORDER.length + keys.indexOf(k) : i;
    };
    return keys.sort((a, b) => rank(a) - rank(b));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      Object.values(row).some((v) => String(v ?? "").toLowerCase().includes(q))
    );
  }, [rows, search]);

  const totals = useMemo(() => {
    const out = {};
    columns.forEach((key) => {
      if (MONEYISH.test(key)) {
        const sum = sumPaise(filtered, key);
        if (sum !== null) out[key] = sum;
      }
    });
    return out;
  }, [filtered, columns]);

  const statusCounts = useMemo(() => {
    const c = { success: 0, failed: 0, pending: 0, other: 0 };
    for (const row of filtered) {
      const s = String(row.status ?? "").toLowerCase();
      if (s.includes("success")) c.success += 1;
      else if (s.includes("fail")) c.failed += 1;
      else if (s.includes("pend") || s.includes("initiate")) c.pending += 1;
      else c.other += 1;
    }
    return c;
  }, [filtered]);

  const exportCsv = () => {
    // Built from what is on screen — the endpoint has no export of its own.
    const esc = (v) => {
      const s = String(v ?? "");
      const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
      return `"${safe.replace(/"/g, '""')}"`;
    };
    const csv = [
      columns.map(label).map(esc).join(","),
      ...filtered.map((r) => columns.map((c) => esc(r[c])).join(",")),
    ].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.replace(/\W+/g, "_").toLowerCase()}_${applied.filter_date}_${applied.end_date}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <ReportPage
      title={title}
      subtitle={subtitle}
      actions={
        <button
          type="button"
          className="apip-btn apip-btn-ghost"
          onClick={exportCsv}
          disabled={!filtered.length}
        >
          <i className="iconoir-download" /> Export CSV
        </button>
      }
    >
      {notice}

      <Card>
        <FilterBar
          onApply={() => setApplied(filters)}
          onReset={() => {
            const fresh = { filter_date: daysAgoISO(defaultDays), end_date: todayISO() };
            setFilters(fresh);
            setApplied(fresh);
            setSearch("");
          }}
          busy={loading}
        >
          <DateField label="From" value={filters.filter_date} onChange={set("filter_date")} max={filters.end_date} />
          <DateField label="To" value={filters.end_date} onChange={set("end_date")} min={filters.filter_date} max={todayISO()} />
          <TextField
            label="Search"
            value={search}
            onChange={setSearch}
            placeholder="Any value in the table"
            hint="Filters the rows already loaded"
          />
        </FilterBar>
      </Card>

      <StatGrid>
        <Stat label="Transactions" value={count(filtered.length)} />
        <Stat label="Success" value={count(statusCounts.success)} tone="ok" />
        <Stat label="Failed" value={count(statusCounts.failed)} tone={statusCounts.failed ? "warn" : ""} />
        <Stat label="Pending" value={count(statusCounts.pending)} tone={statusCounts.pending ? "warn" : ""} />
        {totals.amount && <Stat label="Amount" value={`₹${money(totals.amount)}`} tone="brand" />}
      </StatGrid>

      <Card
        title="Transactions"
        right={
          rows.length > 0 && search
            ? <span className="apip-hint">{filtered.length} of {rows.length} rows match</span>
            : null
        }
      >
        {loading ? (
          <div className="apip-loading">Loading…</div>
        ) : error ? (
          <ErrorState message={error} />
        ) : !filtered.length ? (
          <div className="apip-empty">
            <i className="iconoir-page-flip" />
            <p>
              {rows.length
                ? "No rows match that search."
                : "No transactions in this range."}
            </p>
          </div>
        ) : (
          <>
            <div className="apip-table-wrap">
              <table className="apip-table apip-table-report">
                <thead>
                  <tr>
                    {columns.map((c) => (
                      <th key={c} className={MONEYISH.test(c) ? "apip-num" : ""}>
                        {label(c)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row, i) => (
                    <tr key={row.id ?? i}>
                      {columns.map((c) => (
                        <td key={c} className={MONEYISH.test(c) ? "apip-num" : ""}>
                          {renderCell(c, row[c])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                {Object.keys(totals).length > 0 && (
                  <tfoot>
                    <tr>
                      {columns.map((c, i) => (
                        <td key={c} className={MONEYISH.test(c) ? "apip-num" : ""}>
                          {i === 0 ? "Totals" : totals[c] ? money(totals[c]) : ""}
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            {Object.keys(totals).length > 0 && (
              <p className="apip-hint apip-table-note">
                Totals are summed in the browser from the rows shown — this endpoint
                returns no totals of its own. Reconcile against the ledger before
                using them for anything that matters.
              </p>
            )}
          </>
        )}
      </Card>
    </ReportPage>
  );
}

function renderCell(key, value) {
  if (value === null || value === undefined || value === "") return "—";
  const k = key.toLowerCase();
  if (k === "status") return <Badge value={String(value)} />;
  if (MONEYISH.test(key)) {
    const n = Number(String(value).replace(/,/g, ""));
    return Number.isNaN(n) ? String(value) : money(n);
  }
  if (/(txn|ref|order|ack|rrn|opid)/.test(k)) {
    return <span className="apip-mono">{String(value)}</span>;
  }
  const s = String(value);
  return s.length > 60 ? <span title={s}>{s.slice(0, 57)}…</span> : s;
}

/** Shown on screens whose nav item has no backend endpoint yet. */
export function NoBackendYet({ title, expected }) {
  return (
    <ReportPage title={title}>
      <Notice tone="warn">
        This screen is in the navigation but has no endpoint behind it yet.
        {expected ? ` It would need something like ${expected}.` : ""} Nothing
        is being hidden from you — the API simply does not expose this report.
      </Notice>
      <Card>
        <div className="apip-empty">
          <i className="iconoir-db-error" />
          <p>
            Tell me which endpoint should back this and it is a few lines to
            wire up — the table renders whatever shape the API returns.
          </p>
        </div>
      </Card>
    </ReportPage>
  );
}
